import makeStyles from "@mui/styles/makeStyles";
import { FONT_BODY, FONT_DISPLAY } from "../../utils/designTokens";

// ---- Design tokens -------------------------------------------------------
const INK = "#231A15"; // warm near-black
const PAPER = "#FFF9F0"; // warm cream
const RED = "#FF3008"; // brand red (logo)
const GREEN = "#2E7D32"; // brand green (logo)
const RED_DARK = "#E62B07";
const CREAM = "#FFF6EA";
// Headings and display text use the app's display face (utils/designTokens).
const SERIF = FONT_DISPLAY;
const HAND = FONT_DISPLAY;

// Subtle paper-grain texture (inline SVG noise)
const GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`;

const grainOverlay = (opacity) => ({
  content: '""',
  position: "absolute",
  inset: 0,
  backgroundImage: GRAIN,
  opacity,
  pointerEvents: "none",
  zIndex: 0,
});

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiInputBase-root": {
      color: theme.palette.text.secondary,
    },
    overflowX: "hidden",
    backgroundColor: PAPER,
    "@media (prefers-reduced-motion: reduce)": {
      "& *, & *::before, & *::after": {
        animationDuration: "0.01ms !important",
        animationIterationCount: "1 !important",
        transitionDuration: "0.01ms !important",
      },
    },
  },

  // ---- Hero --------------------------------------------------------------
  hero: {
    position: "relative",
    backgroundColor: PAPER,
    paddingTop: 96,
    paddingBottom: 120,
    overflow: "hidden",
    "&::before": grainOverlay(0.05),
    [theme.breakpoints.down("md")]: {
      paddingTop: 64,
      paddingBottom: 96,
    },
  },
  heroInner: {
    position: "relative",
    zIndex: 1,
  },
  heroGrid: {
    display: "grid",
    gridTemplateColumns: "1.1fr 0.9fr",
    gap: 48,
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: 56,
    },
  },
  heroLeft: {
    [theme.breakpoints.down("md")]: {
      textAlign: "center",
    },
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 14,
    fontFamily: SERIF,
    fontSize: "clamp(1.5rem, 2.9vw, 2.15rem)",
    fontWeight: 800,
    whiteSpace: "nowrap",
    letterSpacing: "-0.01em",
    lineHeight: 1.1,
    color: RED,
    marginBottom: 18,
    [theme.breakpoints.down("sm")]: {
      gap: 10,
      fontSize: "1.15rem",
    },
  },
  liveDot: {
    width: 13,
    height: 13,
    flexShrink: 0,
    borderRadius: "50%",
    backgroundColor: GREEN,
    display: "inline-block",
    animation: "$pulseDot 2s ease-in-out infinite",
  },
  "@keyframes pulseDot": {
    "0%, 100%": { boxShadow: "0 0 0 0 rgba(46,125,50,0.45)" },
    "50%": { boxShadow: "0 0 0 6px rgba(46,125,50,0)" },
  },
  heroTitle: {
    fontFamily: SERIF,
    fontWeight: 800,
    fontSize: "clamp(2.7rem, 5.4vw, 4.5rem)",
    lineHeight: 1.06,
    letterSpacing: "-0.02em",
    color: INK,
    marginBottom: 24,
    [theme.breakpoints.down("sm")]: {
      fontSize: "2.05rem",
    },
  },
  squiggleWrap: {
    position: "relative",
    display: "inline-block",
    whiteSpace: "nowrap",
  },
  squiggle: {
    position: "absolute",
    left: "-2%",
    bottom: "-0.18em",
    width: "104%",
    height: "0.32em",
    overflow: "visible",
    "& path": {
      stroke: GREEN,
      strokeWidth: 7,
      fill: "none",
      strokeLinecap: "round",
      strokeDasharray: 1,
      strokeDashoffset: 1,
      animation: "$drawSquiggle 0.9s cubic-bezier(0.65, 0, 0.35, 1) 0.7s forwards",
    },
  },
  "@keyframes drawSquiggle": {
    to: { strokeDashoffset: 0 },
  },
  heroSubtitle: {
    fontSize: "1.15rem",
    color: "#57493F",
    lineHeight: 1.65,
    marginBottom: 36,
    maxWidth: 520,
    [theme.breakpoints.down("md")]: {
      margin: "0 auto 32px",
    },
  },

  // Search pill
  searchBox: {
    display: "flex",
    gap: 10,
    backgroundColor: "#FFFFFF",
    padding: 8,
    borderRadius: 999,
    border: `2px solid ${INK}`,
    boxShadow: `5px 5px 0 ${INK}`,
    maxWidth: 620,
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    "&:hover": {
      transform: "translate(-2px, -2px)",
      boxShadow: `8px 8px 0 ${INK}`,
    },
    "&:focus-within": {
      transform: "translate(-2px, -2px)",
      boxShadow: `8px 8px 0 ${RED}`,
    },
    [theme.breakpoints.down("md")]: {
      margin: "0 auto",
    },
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      borderRadius: 24,
      padding: 12,
    },
  },
  searchInput: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: 999,
      backgroundColor: "#FFFFFF",
      "& fieldset": { border: "none" },
    },
    "& .MuiInputBase-input": {
      padding: "13px 8px",
      fontSize: "1rem",
      "&::placeholder": {
        color: "#8A7A6D",
        opacity: 1,
      },
    },
  },
  searchButton: {
    backgroundColor: RED,
    color: "#FFFFFF",
    padding: "12px 28px",
    borderRadius: 999,
    fontSize: "1rem",
    fontWeight: 700,
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: RED_DARK,
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "#D8CCC2",
      color: "#FFFFFF",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  statusPill: {
    marginTop: 14,
    maxWidth: 620,
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 18px",
    borderRadius: 999,
    backgroundColor: "#FFFFFF",
    border: `2px solid ${INK}`,
    boxShadow: `3px 3px 0 ${INK}`,
  },
  statusText: {
    fontSize: "0.88rem",
    color: INK,
    fontWeight: 600,
  },
  statusClose: {
    fontSize: "0.88rem",
    color: RED,
    cursor: "pointer",
    fontWeight: 800,
    lineHeight: 1,
  },

  // Hero collage
  heroArt: {
    position: "relative",
    height: 520,
    [theme.breakpoints.down("md")]: {
      height: 440,
      maxWidth: 460,
      margin: "0 auto",
      width: "100%",
    },
    [theme.breakpoints.down("sm")]: {
      height: 380,
    },
  },
  parallaxA: {
    position: "absolute",
    top: 30,
    left: "13%",
    width: "62%",
    zIndex: 2,
    transform: "translate(calc(var(--mx, 0) * 14px), calc(var(--my, 0) * 10px))",
    transition: "transform 0.3s ease-out",
  },
  parallaxB: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "44%",
    zIndex: 1,
    transform: "translate(calc(var(--mx, 0) * -20px), calc(var(--my, 0) * -14px))",
    transition: "transform 0.3s ease-out",
  },
  parallaxC: {
    position: "absolute",
    bottom: -14,
    left: "-4%",
    width: "36%",
    zIndex: 3,
    transform: "translate(calc(var(--mx, 0) * 26px), calc(var(--my, 0) * 18px))",
    transition: "transform 0.3s ease-out",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  polaroid: {
    backgroundColor: "#FFFFFF",
    padding: "10px 10px 14px",
    border: "1px solid rgba(35,26,21,0.08)",
    boxShadow: "0 20px 45px rgba(35,26,21,0.2)",
    "& img": {
      width: "100%",
      display: "block",
      aspectRatio: "1 / 1",
      objectFit: "cover",
    },
  },
  polaroidMain: {
    animation: "$floatMain 7s ease-in-out infinite",
  },
  "@keyframes floatMain": {
    "0%, 100%": { transform: "translateY(0) rotate(-3deg)" },
    "50%": { transform: "translateY(-12px) rotate(-2.2deg)" },
  },
  polaroidSide: {
    animation: "$floatSide 8.5s ease-in-out infinite",
  },
  "@keyframes floatSide": {
    "0%, 100%": { transform: "translateY(0) rotate(4deg)" },
    "50%": { transform: "translateY(-10px) rotate(4.8deg)" },
  },
  polaroidSmall: {
    animation: "$floatSmall 6s ease-in-out infinite",
  },
  "@keyframes floatSmall": {
    "0%, 100%": { transform: "translateY(0) rotate(-7deg)" },
    "50%": { transform: "translateY(-8px) rotate(-5.8deg)" },
  },
  polaroidCaption: {
    display: "block",
    fontFamily: HAND,
    fontWeight: 600,
    fontSize: "1.35rem",
    color: INK,
    textAlign: "center",
    paddingTop: 8,
    lineHeight: 1,
  },
  // ---- Cuisine marquee banner ---------------------------------------------
  marquee: {
    position: "relative",
    backgroundColor: INK,
    transform: "rotate(-1.2deg)",
    width: "104%",
    marginLeft: "-2%",
    marginTop: 10,
    marginBottom: 26,
    zIndex: 3,
    overflow: "hidden",
    padding: "16px 0",
    boxShadow: "0 12px 30px rgba(35,26,21,0.25)",
  },
  marqueeTrack: {
    display: "flex",
    width: "max-content",
    // Home.js sets the duration from the list's width (a steady 30px/s);
    // 55s is only until it has measured.
    animation: "$marquee 55s linear infinite",
    "&:hover": {
      animationPlayState: "paused",
    },
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
    },
  },
  "@keyframes marquee": {
    from: { transform: "translateX(0)" },
    to: { transform: "translateX(-50%)" },
  },
  marqueeGroup: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  marqueeItem: {
    display: "inline-flex",
    alignItems: "center",
    fontFamily: SERIF,
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "1.15rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: CREAM,
    whiteSpace: "nowrap",
    cursor: "pointer",
    "&:hover $marqueeIcon": {
      transform: "scale(1.12)",
    },
  },
  marqueeIcon: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    objectFit: "cover",
    backgroundColor: "#FFFFFF",
    border: "2px solid rgba(255,246,234,0.85)",
    marginRight: 12,
    flexShrink: 0,
    transition: "transform 0.25s ease",
  },
  marqueeStar: {
    color: RED,
    margin: "0 22px",
    fontStyle: "normal",
    fontSize: "0.85rem",
  },

  // ---- Scrolling photo strip -------------------------------------------------
  photoMarquee: {
    overflow: "hidden",
    padding: "12px 0 34px",
  },
  photoTrack: {
    display: "flex",
    width: "max-content",
    animation: "$marquee 46s linear infinite",
    "&:hover": {
      animationPlayState: "paused",
    },
  },
  photoGroup: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  photoTile: {
    flexShrink: 0,
    width: 220,
    margin: "0 16px",
    backgroundColor: "#FFFFFF",
    padding: "8px 8px 12px",
    border: "1px solid rgba(35,26,21,0.08)",
    boxShadow: "0 12px 26px rgba(35,26,21,0.14)",
    transform: "rotate(-2deg)",
    "&:nth-child(even)": {
      transform: "rotate(2deg)",
    },
    "& img": {
      width: "100%",
      height: 150,
      display: "block",
      objectFit: "cover",
    },
  },

  // ---- Shared section bits -------------------------------------------------
  section: {
    position: "relative",
    padding: "96px 0",
    [theme.breakpoints.down("md")]: {
      padding: "72px 0",
    },
  },
  sectionInner: {
    position: "relative",
    zIndex: 1,
  },
  sectionTitle: {
    fontFamily: SERIF,
    fontWeight: 800,
    fontSize: "clamp(2rem, 4vw, 3rem)",
    letterSpacing: "-0.02em",
    color: INK,
    textAlign: "center",
    marginBottom: 64,
    [theme.breakpoints.down("sm")]: {
      marginBottom: 44,
    },
  },

  // ---- Feature cards (Delivery & Pickup / Top Restaurants) --------------------
  featuresGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 32,
    maxWidth: 1200,
    margin: "0 auto",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: 28,
    },
  },
  featureCard: {
    height: "100%",
    boxSizing: "border-box",
    backgroundColor: "#FFFFFF",
    border: `2px solid ${INK}`,
    borderRadius: 22,
    padding: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 28,
    boxShadow: `6px 6px 0 ${INK}`,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "translate(-3px, -5px)",
      boxShadow: `10px 12px 0 ${RED}`,
    },
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      textAlign: "center",
      padding: 28,
    },
  },
  featureCardContent: {
    flex: 1,
  },
  featureCardLabel: {
    fontSize: "0.85rem",
    fontWeight: 800,
    color: RED,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    marginBottom: 12,
  },
  featureCardTitle: {
    fontFamily: SERIF,
    fontSize: "1.7rem",
    fontWeight: 700,
    color: INK,
    marginBottom: 12,
    lineHeight: 1.15,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.4rem",
    },
  },
  featureCardDesc: {
    fontSize: "1rem",
    color: "#57493F",
    lineHeight: 1.65,
  },
  featureCardImage: {
    width: 170,
    flexShrink: 0,
    backgroundColor: "#FFFFFF",
    padding: "6px 6px 8px",
    border: "1px solid rgba(35,26,21,0.08)",
    boxShadow: "0 10px 22px rgba(35,26,21,0.16)",
    transform: "rotate(3deg)",
    "& img": {
      width: "100%",
      height: 140,
      display: "block",
      objectFit: "cover",
    },
    [theme.breakpoints.down("sm")]: {
      width: 150,
    },
  },

  // ---- How it works ---------------------------------------------------------
  stepsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 36,
    maxWidth: 1040,
    margin: "0 auto",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: 40,
      maxWidth: 460,
    },
  },
  stepCard: {
    position: "relative",
    backgroundColor: "#FFFFFF",
    border: `2px solid ${INK}`,
    borderRadius: 20,
    padding: "44px 28px 32px",
    textAlign: "center",
    boxShadow: `6px 6px 0 ${INK}`,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "rotate(0deg) translateY(-6px)",
      boxShadow: `9px 11px 0 ${RED}`,
    },
  },
  stepCardA: { transform: "rotate(-1.6deg)" },
  stepCardB: { transform: "rotate(1.1deg)" },
  stepCardC: { transform: "rotate(-0.8deg)" },
  stepNumber: {
    position: "absolute",
    top: -26,
    left: "50%",
    marginLeft: -26,
    width: 52,
    height: 52,
    borderRadius: "50%",
    backgroundColor: RED,
    color: "#FFFFFF",
    fontFamily: SERIF,
    fontWeight: 800,
    fontSize: "1.3rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `2px solid ${INK}`,
    boxShadow: `3px 3px 0 ${INK}`,
    transform: "rotate(-6deg)",
  },
  stepTitle: {
    fontFamily: SERIF,
    fontSize: "1.45rem",
    fontWeight: 700,
    color: INK,
    marginBottom: 10,
  },
  stepDesc: {
    fontSize: "1rem",
    color: "#57493F",
    lineHeight: 1.65,
  },

  // ---- Value polaroid trio ---------------------------------------------------
  valueSection: {
    backgroundColor: "#FFF3E4",
  },
  valueGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 44,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: 56,
      maxWidth: 420,
      margin: "0 auto",
    },
  },
  valueCard: {
    textAlign: "center",
  },
  valuePolaroid: {
    backgroundColor: "#FFFFFF",
    padding: "12px 12px 16px",
    border: "1px solid rgba(35,26,21,0.08)",
    boxShadow: "0 16px 36px rgba(35,26,21,0.16)",
    marginBottom: 26,
    transition: "transform 0.35s ease, box-shadow 0.35s ease",
    "& img": {
      width: "100%",
      height: 240,
      display: "block",
      objectFit: "cover",
    },
    "$valueCard:hover &": {
      transform: "rotate(0deg) translateY(-6px)",
      boxShadow: "0 24px 48px rgba(35,26,21,0.22)",
    },
  },
  tiltLeft: {
    transform: "rotate(-2deg)",
  },
  tiltRight: {
    transform: "rotate(2deg)",
  },
  valueTitle: {
    fontFamily: SERIF,
    fontSize: "1.5rem",
    fontWeight: 700,
    color: INK,
    marginBottom: 10,
  },
  valueDesc: {
    fontSize: "1rem",
    color: "#57493F",
    lineHeight: 1.7,
    maxWidth: 320,
    margin: "0 auto",
  },

  // ---- About (dark editorial band) --------------------------------------------
  aboutSection: {
    position: "relative",
    backgroundColor: INK,
    overflow: "hidden",
    "&::before": grainOverlay(0.09),
  },
  aboutGrid: {
    display: "grid",
    gridTemplateColumns: "1.05fr 0.95fr",
    gap: 72,
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: 48,
    },
  },
  aboutContent: {
    [theme.breakpoints.down("md")]: { textAlign: "center" },
  },
  aboutTitle: {
    fontFamily: SERIF,
    fontWeight: 800,
    fontSize: "clamp(2rem, 3.6vw, 2.9rem)",
    letterSpacing: "-0.02em",
    color: CREAM,
    lineHeight: 1.15,
    marginBottom: 22,
  },
  aboutDesc: {
    fontSize: "1.12rem",
    color: "rgba(255,246,234,0.78)",
    lineHeight: 1.75,
    marginBottom: 34,
  },
  aboutButton: {
    borderColor: CREAM,
    color: CREAM,
    padding: "13px 30px",
    borderRadius: 999,
    fontSize: "1rem",
    fontWeight: 700,
    textTransform: "none",
    borderWidth: 2,
    "& .MuiButton-endIcon": {
      transition: "transform 0.25s ease",
    },
    "&:hover": {
      borderColor: RED,
      color: "#FFFFFF",
      backgroundColor: "rgba(255,48,8,0.18)",
      borderWidth: 2,
      "& .MuiButton-endIcon": {
        transform: "translateX(4px)",
      },
    },
  },
  aboutPolaroid: {
    backgroundColor: "#FFFFFF",
    padding: "12px 12px 16px",
    transform: "rotate(2deg)",
    boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
    maxWidth: 520,
    margin: "0 auto",
    transition: "transform 0.35s ease",
    "&:hover": {
      transform: "rotate(0.5deg) scale(1.015)",
    },
    "& img": {
      width: "100%",
      height: 380,
      display: "block",
      objectFit: "cover",
    },
    [theme.breakpoints.down("sm")]: {
      "& img": { height: 280 },
    },
  },

  // ---- Partner / Driver postcards ---------------------------------------------
  postcardsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 44,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: 48,
      maxWidth: 520,
      margin: "0 auto",
    },
  },
  postcard: {
    position: "relative",
    height: "100%",
    boxSizing: "border-box",
    backgroundColor: "#FFFFFF",
    border: `2px solid ${INK}`,
    borderRadius: 22,
    overflow: "hidden",
    boxShadow: `7px 7px 0 ${INK}`,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "translate(-3px, -5px)",
      boxShadow: `12px 14px 0 ${RED}`,
    },
  },
  postcardImage: {
    position: "relative",
    "& img": {
      width: "100%",
      height: 260,
      display: "block",
      objectFit: "cover",
    },
  },
  postcardStamp: {
    position: "absolute",
    top: 16,
    right: 16,
    backgroundColor: "#FFB800",
    color: INK,
    fontWeight: 800,
    fontSize: "0.78rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    padding: "8px 14px",
    border: `2px solid ${INK}`,
    borderRadius: 8,
    transform: "rotate(4deg)",
    boxShadow: `2px 2px 0 ${INK}`,
  },
  postcardBody: {
    padding: "30px 30px 34px",
  },
  postcardTitle: {
    fontFamily: SERIF,
    fontWeight: 800,
    fontSize: "1.7rem",
    letterSpacing: "-0.01em",
    color: INK,
    lineHeight: 1.15,
    marginBottom: 12,
  },
  postcardDesc: {
    fontSize: "1rem",
    color: "#57493F",
    lineHeight: 1.7,
    marginBottom: 26,
  },
  postcardButton: {
    backgroundColor: INK,
    color: CREAM,
    padding: "12px 26px",
    borderRadius: 999,
    fontSize: "0.95rem",
    fontWeight: 700,
    textTransform: "none",
    boxShadow: "none",
    "& .MuiButton-endIcon": {
      transition: "transform 0.25s ease",
    },
    "&:hover": {
      backgroundColor: RED,
      boxShadow: "none",
      "& .MuiButton-endIcon": {
        transform: "translateX(4px)",
      },
    },
  },

  // ---- App download --------------------------------------------------------
  appSection: {
    backgroundColor: "#FFF3E4",
  },
  appGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 72,
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: 48,
    },
  },
  appContent: {
    [theme.breakpoints.down("md")]: { textAlign: "center" },
  },
  appTitle: {
    fontFamily: SERIF,
    fontWeight: 800,
    fontSize: "clamp(2rem, 3.6vw, 2.9rem)",
    letterSpacing: "-0.02em",
    color: INK,
    lineHeight: 1.15,
    marginBottom: 18,
  },
  appDesc: {
    fontSize: "1.12rem",
    color: "#57493F",
    lineHeight: 1.7,
    marginBottom: 30,
  },
  appBadges: {
    display: "flex",
    gap: 16,
    [theme.breakpoints.down("md")]: { justifyContent: "center" },
    [theme.breakpoints.down("sm")]: { flexWrap: "wrap" },
    "& a": {
      display: "block",
      transition: "transform 0.25s ease",
      "&:hover": {
        transform: "translateY(-3px)",
      },
    },
  },
  appBadge: {
    height: 52,
    display: "block",
  },
  appArt: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
  },
  phoneFrame: {
    position: "relative",
    width: 290,
    padding: 13,
    backgroundColor: "#17120F",
    borderRadius: 52,
    boxShadow:
      "0 34px 70px rgba(35,26,21,0.38), inset 0 0 0 2px rgba(255,255,255,0.09)",
    animation: "$floatPhone 7s ease-in-out infinite",
    // side buttons
    "&::before": {
      content: '""',
      position: "absolute",
      right: -3,
      top: 150,
      width: 3,
      height: 68,
      borderRadius: 2,
      backgroundColor: "#2E2620",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: -3,
      top: 120,
      width: 3,
      height: 100,
      borderRadius: 2,
      backgroundColor: "#2E2620",
    },
    "& img": {
      width: "100%",
      display: "block",
      borderRadius: 39,
    },
    [theme.breakpoints.down("sm")]: {
      width: 240,
      padding: 11,
      borderRadius: 44,
      "& img": { borderRadius: 33 },
    },
  },
  "@keyframes floatPhone": {
    "0%, 100%": { transform: "translateY(0) rotate(2deg)" },
    "50%": { transform: "translateY(-11px) rotate(2.8deg)" },
  },
  phoneIsland: {
    position: "absolute",
    top: 25,
    left: "50%",
    transform: "translateX(-50%)",
    width: 82,
    height: 21,
    borderRadius: 999,
    backgroundColor: "#000000",
    zIndex: 2,
    [theme.breakpoints.down("sm")]: {
      top: 21,
      width: 68,
      height: 18,
    },
  },
  phoneScreen: {
    position: "relative",
  },
  // Enlarged app icon + name overlaid on the (tiny) one in the screenshot
  phoneAppOverlay: {
    position: "absolute",
    top: "36%",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    zIndex: 1,
  },
  phoneAppTile: {
    width: 112,
    height: 112,
    borderRadius: 28,
    backgroundColor: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 43,
    boxShadow: "0 10px 26px rgba(0,0,0,0.55)",
    [theme.breakpoints.down("sm")]: {
      width: 92,
      height: 92,
      borderRadius: 23,
      fontSize: 35,
    },
  },
  phoneAppName: {
    color: "#FFFFFF",
    fontSize: "1.02rem",
    fontWeight: 600,
    fontFamily: FONT_BODY,
    letterSpacing: "0.01em",
    textShadow: "0 2px 8px rgba(0,0,0,0.8)",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.88rem",
    },
  },
  phoneGloss: {
    position: "absolute",
    inset: 13,
    borderRadius: 39,
    background:
      "linear-gradient(115deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 32%, transparent 58%)",
    pointerEvents: "none",
    [theme.breakpoints.down("sm")]: {
      inset: 11,
      borderRadius: 33,
    },
  },

  // ---- Final CTA -------------------------------------------------------------
  ctaSection: {
    position: "relative",
    backgroundColor: RED,
    textAlign: "center",
    overflow: "hidden",
    "&::before": grainOverlay(0.08),
  },
  ctaTitle: {
    fontFamily: SERIF,
    fontWeight: 800,
    fontStyle: "normal",
    fontSize: "clamp(2.2rem, 5vw, 3.6rem)",
    letterSpacing: "-0.02em",
    color: "#FFFFFF",
    marginBottom: 14,
  },
  ctaSubtitle: {
    fontSize: "1.15rem",
    color: "rgba(255,255,255,0.92)",
    marginBottom: 36,
  },
  ctaButton: {
    backgroundColor: "#FFFFFF",
    color: INK,
    padding: "16px 42px",
    borderRadius: 999,
    fontSize: "1.05rem",
    fontWeight: 800,
    textTransform: "none",
    border: `2px solid ${INK}`,
    boxShadow: `5px 5px 0 ${INK}`,
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    "&:hover": {
      backgroundColor: CREAM,
      transform: "translate(-2px, -2px)",
      boxShadow: `8px 8px 0 ${INK}`,
    },
  },

  // ---- Footer ---------------------------------------------------------------
  footerContainer: {
    background: "#1A1A1A",
    width: "100%",
  },
  footerWrapper: {
    backgroundColor: "#1A1A1A",
    width: "100%",
    display: "flex",
  },
}));

export default useStyles;